<script setup lang="ts">
import { ref, watch } from 'vue';

export interface Props {
  src: string;
  alt?: string;
  fallbackSrc?: string;
  eager?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  eager: false,
});

enum ImageState {
  Loading = 'loading',
  Loaded = 'loaded',
  Error = 'error',
}

const state = ref<ImageState>(ImageState.Loading);
const currentSrc = ref('');

function setSrc(src: string) {
  const resolved = src || props.fallbackSrc || '';
  currentSrc.value = resolved;
  state.value = resolved ? ImageState.Loading : ImageState.Error;
}

setSrc(props.src);

function onLoad() {
  state.value = ImageState.Loaded;
}

function onError() {
  if (props.fallbackSrc && currentSrc.value !== props.fallbackSrc) {
    currentSrc.value = props.fallbackSrc;
  } else {
    state.value = ImageState.Error;
  }
}

watch(() => props.src, setSrc);
</script>

<template>
  <div
    class="backstage-image-container"
    :class="{ 'is-loading': state === ImageState.Loading, 'has-error': state === ImageState.Error }">
    <img
      v-if="currentSrc && state !== ImageState.Error"
      :key="currentSrc"
      :loading="eager ? 'eager' : 'lazy'"
      :src="currentSrc"
      :alt="alt"
      decoding="async"
      v-bind="$attrs"
      @load="onLoad"
      @error="onError"
      data-cy="product-image" />
    <div v-if="state === ImageState.Loading" class="image-loading" data-cy="image-loading">
      <div class="loader"></div>
    </div>
    <div v-if="state === ImageState.Error" class="image-placeholder" data-cy="image-placeholder">
      <div class="placeholder-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 19 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
            fill="currentColor" />
        </svg>
      </div>
      <span class="placeholder-text">{{ $t('global.image_error') }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.backstage-image-container {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: opacity 0.3s ease;

    &:not([src]) {
      opacity: 0;
    }
  }

  .image-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-text-color);
    border-radius: 4px;
    z-index: 1;
  }

  .image-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    color: var(--secondary-text-color, #6c757d);
    text-align: center;

    .placeholder-icon {
      margin-bottom: 0.25rem;
      opacity: 0.6;
    }

    .placeholder-text {
      font-size: 0.75rem;
      max-width: 100%;
      word-wrap: break-word;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.is-loading img {
    opacity: 0.5;
  }

  &.has-error {
    .image-placeholder {
      color: var(--primary-text-color);
    }
  }
}

@media (max-width: 768px) {
  .backstage-image-container {
    .image-placeholder {
      min-height: 80px;
      padding: 0.5rem;

      .placeholder-text {
        font-size: 0.75rem;
      }
    }
  }
}
</style>
